import { useState } from "react";
import {
  Image as ImageIcon,
  ExternalLink,
  Upload,
  Loader2,
} from "lucide-react";
import RichTextEditor from "./RichTextEditor";
import Repeater from "./Repeater";
import { api } from "@/lib/api";

/** Image field: URL input + preview */
function ImageField({ value, onChange, label, testid }) {
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return;

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post(
        "/upload/image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      onChange(data.url);
    } catch (err) {
      console.error(err);
      alert("Image upload failed.");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-3">

      <div className="flex items-center gap-3">

        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#538135] text-[#538135] cursor-pointer hover:bg-[#F4F8F1] transition">

          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload size={16} />
              Upload Image
            </>
          )}

          <input
            type="file"
            accept="image/*"
            hidden
            disabled={uploading}
            onChange={(e) => uploadImage(e.target.files?.[0])}
          />
        </label>

        <span className="text-xs text-gray-500">
          or paste an image URL below
        </span>

      </div>
      <input
        data-testid={testid}
        type="url"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://…/image.jpg"
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
      />
      {value ? (
        <div className="mt-2 relative border border-[#E5E7EB] rounded-lg overflow-hidden bg-[#F8FAF7]">
          <img
            src={value} alt={label || ""}
            className="w-full max-h-56 object-cover"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
          <div style={{ display: "none" }} className="h-40 items-center justify-center text-gray-400 text-xs">
            <ImageIcon size={20} className="mr-2" /> Could not load image
          </div>
          <a href={value} target="_blank" rel="noreferrer"
            className="absolute top-2 right-2 bg-white/90 hover:bg-white p-1.5 rounded shadow-sm text-gray-600">
            <ExternalLink size={12} />
          </a>
        </div>
      ) : (
        <div className="mt-2 h-24 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xs">
          <ImageIcon size={16} className="mr-2" /> No image
        </div>
      )}
    </div>
  );
}

/** Link field: label + href */
function LinkField({ value, onChange, testid }) {
  const v = value || { label: "", href: "" };
  return (
    <div className="grid grid-cols-2 gap-2">
      <input
        data-testid={`${testid}-label`}
        value={v.label || ""}
        onChange={(e) => onChange({ ...v, label: e.target.value })}
        placeholder="Label"
        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
      />
      <input
        data-testid={`${testid}-href`}
        value={v.href || ""}
        onChange={(e) => onChange({ ...v, href: e.target.value })}
        placeholder="/path or https://…"
        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
      />
    </div>
  );
}

/** Simple list of strings */
function StringListField({ value, onChange, testid }) {
  const items = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...items, draft.trim()]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          data-testid={`${testid}-input`}
          value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Type & press Enter"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
        />
        <button
          type="button" onClick={add}
          data-testid={`${testid}-add`}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
        >
          Add
        </button>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((it, i) => (
            <li key={i} className="flex items-center justify-between bg-[#F8FAF7] border border-[#E5E7EB] rounded px-3 py-2 text-sm">
              <span>{it}</span>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="text-gray-400 hover:text-[#DC2626]"
              >×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Generic field renderer — dispatches to the right widget based on `field.type`.
 */
export default function FieldRenderer({ field, value, onChange, path = "" }) {
  const testid = `field-${(path + "-" + field.key).replace(/^-+/, "")}`;

  return (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
        {field.label}
        {field.required && <span className="text-[#DC2626]"> *</span>}
      </label>
      {field.hint && <p className="text-[11px] text-gray-500 mb-1.5">{field.hint}</p>}

      {field.type === "text" && (
        <input
          data-testid={testid}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
        />
      )}

      {field.type === "textarea" && (
        <textarea
          data-testid={testid} rows={3}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
        />
      )}

      {field.type === "select" && (
        <select
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
        >
          {(field.options || []).map((option) => {
            if (typeof option === "string") {
              return (
                <option key={option} value={option}>
                  {option}
                </option>
              );
            }

            return (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            );
          })}
        </select>
      )}
      {field.type === "checkbox" && (
        <input
          data-testid={testid}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-[#538135] focus:ring-[#538135]"
        />
      )}

      {field.type === "richtext" && (
        <RichTextEditor value={value || ""} onChange={onChange} testid={testid} />
      )}

      {field.type === "image" && (
        <ImageField value={value} onChange={onChange} label={field.label} testid={testid} />
      )}

      {field.type === "link" && (
        <LinkField value={value} onChange={onChange} testid={testid} />
      )}

      {field.type === "list" && (
        <StringListField value={value} onChange={onChange} testid={testid} />
      )}

      {field.type === "repeater" && (
        <Repeater
          field={field}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          path={testid}
        />
      )}
    </div>
  );
}
